/**
 * Questions about, and edits to, a whole sharing config: every intent's
 * form at once. A slot is named by its id, which no other slot in the config
 * shares, and a form by its intent.
 *
 * Soft links live here because they span forms. A slot like Greeting is
 * usually asked the same way in the intro, the reminder and the update.
 * Rather than authoring it three times, the author links it: the builder's
 * link popup lets the author tick the intents the slot should be linked with.
 * Each ticked intent gets a copy, and the original and its copies carry the
 * same `linkId` (see the schema), one slot per intent at most. Unticking an
 * intent leaves its slot behind as an independent one.
 *
 * A link is "soft" because nothing ties the slots' contents together: each is
 * an ordinary slot in its own form, keeps its own id, and stays valid on its
 * own. The link only records that they are meant to be the same, so the
 * builder can carry an edit across and the composer can ask the sender once.
 */

import type { MessageIntent } from '@/modules/template/types';
import { SlotLogic } from './slot_logic';
import type { SharingFormSlot, SharingConfig } from './types';

/** The intent whose form holds the slot `slotId` names, if any form does. */
function intentOf(
  config: SharingConfig,
  slotId: string
): MessageIntent | undefined {
  return config.forms.find(f => f.form.some(slot => slot.id === slotId))?.intent;
}

/** The slot `slotId` names, in whichever intent's form holds it. */
function slotWithId(
  config: SharingConfig,
  slotId: string
): SharingFormSlot | undefined {
  for (const f of config.forms) {
    const slot = SlotLogic.slotWithId(f.form, slotId);
    if (slot) return slot;
  }
  return undefined;
}

/**
 * The ids of every other slot soft linked to the one `slotId` names, in form
 * order. Empty when that slot is linked to nothing.
 */
function linkedPeers(config: SharingConfig, slotId: string): string[] {
  const slot = slotWithId(config, slotId);
  const linkId = slot ? SlotLogic.linkIdOf(slot) : null;
  if (linkId === null) return [];
  return config.forms.flatMap(f =>
    f.form.filter(s => s.linkId === linkId && s.id !== slotId).map(s => s.id)
  );
}

/**
 * The intents the slot `slotId` names can be linked into: every other intent
 * whose form does not already hold a slot of its link, in config order.
 */
function linkTargets(config: SharingConfig, slotId: string): MessageIntent[] {
  const home = intentOf(config, slotId);
  if (home === undefined) return [];
  const taken = linkedPeers(config, slotId).map(peer => intentOf(config, peer));
  return config.forms
    .map(f => f.intent)
    .filter(intent => intent !== home && !taken.includes(intent));
}

/**
 * Link the slot `slotId` names into `target`'s form. The original takes a
 * fresh link id unless it already has one, so linking again from any member
 * grows the same set.
 *
 * When `target`'s form already holds an unlinked slot asking exactly the same
 * thing (`SlotLogic.sameContent`), that slot joins the link where it stands,
 * keeping its id and so the answers stored under it. Otherwise the form gets a
 * copy with a fresh slot id, so it stores its answer apart, at the original's
 * index (or the end of a shorter form), where the same question most likely
 * sits in a form laid out like this one. `linkedId` is the id of the slot that
 * joined, whichever it was.
 *
 * Returns the config unchanged, and no linked id, when no slot has that id or
 * `target` is not one of its `linkTargets`.
 */
function linkInto(
  config: SharingConfig,
  slotId: string,
  target: MessageIntent
): { config: SharingConfig; linkedId: string | null } {
  const home = intentOf(config, slotId);
  const slot = slotWithId(config, slotId);
  const homeForm = config.forms.find(f => f.intent === home)?.form;
  const targetForm = config.forms.find(f => f.intent === target)?.form;
  if (
    !slot ||
    !homeForm ||
    !targetForm ||
    !linkTargets(config, slotId).includes(target)
  ) {
    return { config, linkedId: null };
  }
  const linkId = SlotLogic.linkIdOf(slot) ?? SlotLogic.newSlotId();
  const original = { ...slot, linkId };
  const twin = targetForm.find(
    s => s.linkId === undefined && SlotLogic.sameContent(s, slot)
  );
  const index = Math.min(
    homeForm.findIndex(s => s.id === slotId),
    targetForm.length
  );
  const joined = twin
    ? { ...twin, linkId }
    : SlotLogic.withId(original, SlotLogic.newSlotId());
  return {
    config: {
      ...config,
      forms: config.forms.map(f => {
        if (f.intent === home) {
          return {
            ...f,
            form: SlotLogic.replaceSlot(f.form, slotId, original),
          };
        }
        if (f.intent === target) {
          return {
            ...f,
            form: twin
              ? SlotLogic.replaceSlot(f.form, twin.id, joined)
              : SlotLogic.insertSlot(f.form, index, joined),
          };
        }
        return f;
      }),
    },
    linkedId: joined.id,
  };
}

/**
 * Link the slot `slotId` names with exactly the intents in `linked`, the way
 * the author ticks them in the link popup. A ticked intent without a member
 * is linked in (`linkInto`); a member in an intent left unticked stays
 * where it is, as an independent slot, still reading as it did when it was
 * linked. The slot's own intent is listed too: left unticked, the slot
 * leaves the set and the slots it was linked with stay linked to each other.
 * Ticking nothing unlinks the whole set.
 */
function setLinkedIntents(
  config: SharingConfig,
  slotId: string,
  linked: readonly MessageIntent[]
): SharingConfig {
  const home = intentOf(config, slotId);
  if (home === undefined) return config;
  const peers = linkedPeers(config, slotId).map(peer => ({
    slotId: peer,
    intent: intentOf(config, peer),
  }));
  const kept = peers.map(peer => peer.intent);
  const dropped = peers
    .filter(peer => peer.intent === undefined || !linked.includes(peer.intent))
    .map(peer => peer.slotId);
  let next: SharingConfig = {
    ...config,
    forms: config.forms.map(f => ({
      ...f,
      form: f.form.map(slot =>
        dropped.includes(slot.id) ? SlotLogic.withoutLink(slot) : slot
      ),
    })),
  };
  for (const target of linked) {
    if (target !== home && !kept.includes(target)) {
      next = linkInto(next, slotId, target).config;
    }
  }
  if (!linked.includes(home)) {
    next = {
      ...next,
      forms: next.forms.map(f => ({
        ...f,
        form: f.form.map(slot =>
          slot.id === slotId ? SlotLogic.withoutLink(slot) : slot
        ),
      })),
    };
  }
  return withoutLoneLinks(next);
}

/**
 * Drop every link only one slot still carries. A link is between slots, so
 * once its other members are deleted or reverted away the last one is linked
 * to nothing, and says so.
 */
function withoutLoneLinks(config: SharingConfig): SharingConfig {
  const counts = new Map<string, number>();
  for (const f of config.forms) {
    for (const slot of f.form) {
      if (slot.linkId !== undefined) {
        counts.set(slot.linkId, (counts.get(slot.linkId) ?? 0) + 1);
      }
    }
  }
  const lone = (slot: SharingFormSlot) =>
    slot.linkId !== undefined && counts.get(slot.linkId) === 1;
  if (!config.forms.some(f => f.form.some(lone))) return config;
  return {
    ...config,
    forms: config.forms.map(f =>
      f.form.some(lone)
        ? {
            ...f,
            form: f.form.map(slot =>
              lone(slot) ? SlotLogic.withoutLink(slot) : slot
            ),
          }
        : f
    ),
  };
}

/**
 * The config with the slot `slotId` names copied over each slot linked to it,
 * each keeping its own id, which names it and stores its answer, and its
 * link id.
 */
function applyToLinked(
  config: SharingConfig,
  slotId: string
): SharingConfig {
  const source = slotWithId(config, slotId);
  const peers = new Set(linkedPeers(config, slotId));
  if (source === undefined || peers.size === 0) return config;
  return {
    ...config,
    forms: config.forms.map(f =>
      f.form.some(slot => peers.has(slot.id))
        ? {
            ...f,
            form: f.form.map(slot =>
              peers.has(slot.id)
                ? // Spread over the source, so the copy keeps its keys in
                  // the source's order and dumps the way the source does.
                  { ...source, id: slot.id, linkId: slot.linkId }
                : slot
            ),
          }
        : f
    ),
  };
}

/**
 * `after`, an edit of `before`, with any change it made to the slot `openId`
 * names (the card open while it was made) carried to the slots linked to it,
 * so linked slots always read the same. An author who wants one to differ
 * unlinks it first. A delete leaves no such slot, and a duplicate leaves it
 * unchanged, so neither carries anything.
 */
function carryEdit(
  before: SharingConfig,
  after: SharingConfig,
  openId: string | null
): SharingConfig {
  if (openId === null) return after;
  const was = slotWithId(before, openId);
  const is = slotWithId(after, openId);
  if (was === undefined || is === undefined) return after;
  if (SlotLogic.linkIdOf(is) === null || SlotLogic.sameContent(was, is)) {
    return after;
  }
  return applyToLinked(after, openId);
}

/**
 * The config with every intent's form replaced by the slots edited for it.
 * `slots` is keyed by form position: `slots[j]` is the edited form of
 * `config.forms[j]`, the way the builder holds one slot stack per tab. A form
 * with no entry keeps the slots it has.
 */
function updateSlots(
  config: SharingConfig,
  slots: readonly (readonly SharingFormSlot[])[]
): SharingConfig {
  return {
    ...config,
    forms: config.forms.map((f, j) => ({
      intent: f.intent,
      form: [...(slots[j] ?? f.form)],
    })),
  };
}

export const SharingConfigLogic = {
  intentOf,
  slotWithId,
  linkedPeers,
  linkTargets,
  linkInto,
  setLinkedIntents,
  withoutLoneLinks,
  applyToLinked,
  carryEdit,
  updateSlots,
};
